import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components/macro'
import { themeGet } from '@styled-system/theme-get'
import {
  UserRecord,
  UserState,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import palette from 'src/designSystem/variables/palette'
import { Owner as OwnerEnum } from 'src/Utils/globalEnums'
import { PERMISSIONS } from 'src/config/roleConfig'
import { useUsers } from 'src/components/async/Users/useUsers'
import {
  AssigneeDisplay,
  useAssigneeDisplayNames,
} from 'src/components/Reusable/AssigneeDisplay'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import { DROPDOWN_SEARCH_THRESHOLD } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { UserSelectLabel } from 'src/components/Reusable/UserSelectLabel'
import { UnassignedIcon } from 'src/components/Gravatar/styles'
import { DropdownSVG } from 'src/components/Icons/Basic'
import { DropdownLabel } from 'src/components/Reusable/Dropdown/styles'
import { SelectDropdownLabel } from 'src/components/Reusable/SelectDropdown/SelectDropdown.styles'
import { PlainTooltip } from 'src/components/Reusable/Tooltip/PlainTooltip/PlainTooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import useClickOutside from 'src/Utils/useClickOutside'

const AssigneeToggleBox = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 8px 0 12px;
  border: 1px solid ${palette.neutral[200]};
  border-radius: 8px;
  background-color: ${({ $isOpen }) => ($isOpen ? '#515d75' : 'white')};
  cursor: pointer;
  gap: 8px;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      color: ${palette.neutral.white};
      ${UnassignedIcon} {
        color: ${palette.neutral.white};
      }
    `}
`

const AssigneeToggleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  flex: 1;
`

const AssigneeDropdownPanel = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  width: 320px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  margin-top: 4px;
`

const AssigneeUserList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`

const AssigneeDropdownItem = styled.button`
  width: 100%;
  padding: 4px 12px;
  border: none;
  background: white;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }

  .form-check {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    padding: 0;
    margin: 0;
  }

  .form-check-label {
    cursor: pointer;
    text-align: left;
    display: flex;
    align-items: center;
  }
`

const AssigneeFieldWrap = styled.div`
  position: relative;
  width: 100%;
`

const StyledDropdownChevron = styled(DropdownSVG)`
  flex-shrink: 0;
  transform: scaleY(1);
  transition: transform 0.35s;
  fill: ${themeGet('colors.text.icon.low')};

  button[aria-expanded='true'] > & {
    transform: scaleY(-1);
  }
`

type ShowPageAssigneesFieldProps = {
  modelId: string
  assigneeEmails: string[]
  isDisabled?: boolean
  hideLabel?: boolean
  onSave: (emails: string[]) => Promise<void>
}

export const ShowPageAssigneesField = ({
  modelId,
  assigneeEmails,
  isDisabled = false,
  hideLabel = false,
  onSave,
}: ShowPageAssigneesFieldProps): JSX.Element => {
  const response = useUsers()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapRef = useClickOutside(() => {
    setIsOpen(false)
    setSearch('')
  }) as React.RefObject<HTMLDivElement>

  const [localEmails, setLocalEmails] = useState<string[]>(assigneeEmails)
  useEffect(() => {
    setLocalEmails(assigneeEmails)
  }, [assigneeEmails])

  const activeUsers = useMemo(() => {
    if (!response.data) return []
    return response.data
      .getUsersList()
      .filter((u: UserRecord) => u.getState() === UserState.USER_ACTIVE)
      .sort((a: UserRecord, b: UserRecord) =>
        (a.getEmail() ?? '').localeCompare(b.getEmail() ?? ''),
      )
  }, [response.data])

  const filteredUsers = useMemo(() => {
    if (!search) return activeUsers
    const q = search.toLowerCase()
    return activeUsers.filter(
      (u: UserRecord) =>
        u.getEmail()?.toLowerCase().includes(q) ||
        u.getName()?.toLowerCase().includes(q),
    )
  }, [activeUsers, search])

  const selectedSet = useMemo(() => new Set(localEmails), [localEmails])
  const { displayName } = useAssigneeDisplayNames()
  const tooltipBody = localEmails.length
    ? localEmails.map(displayName).join(', ')
    : undefined

  const toggle = async (email: string) => {
    const next = new Set(localEmails)
    next.has(email) ? next.delete(email) : next.add(email)
    const emails = Array.from(next)
    setLocalEmails(emails)
    try {
      await onSave(emails)
    } catch (err) {
      // revert on failure
      setLocalEmails(localEmails)
    }
  }

  return (
    <DropdownLabel>
      {!hideLabel && <SelectDropdownLabel>Assignees</SelectDropdownLabel>}
      <AssigneeFieldWrap ref={wrapRef}>
        <PlainTooltip
          id={`assignee-field-tooltip-${modelId}`}
          tooltipBody={tooltipBody || ''}
          placement={TooltipPositions.top}
          disabled={!tooltipBody}
        >
          <AssigneeToggleBox
            type="button"
            $isOpen={isOpen}
            aria-expanded={isOpen}
            aria-label={hideLabel ? 'Assignees' : undefined}
            disabled={isDisabled}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsOpen((s) => !s)
            }}
          >
            <AssigneeToggleLeft>
              {localEmails.length === 0 ? (
                <UserSelectLabel
                  email={OwnerEnum.UNASSIGNED}
                  name={OwnerEnum.UNASSIGNED}
                />
              ) : (
                <AssigneeDisplay emails={localEmails} />
              )}
            </AssigneeToggleLeft>
            <StyledDropdownChevron />
          </AssigneeToggleBox>
        </PlainTooltip>
        <AssigneeDropdownPanel
          $isOpen={isOpen}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {(activeUsers.length > DROPDOWN_SEARCH_THRESHOLD || !!search) && (
            <DropdownSearchInput
              value={search}
              onChange={setSearch}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              autoFocus={isOpen}
            />
          )}
          <AssigneeUserList>
            {filteredUsers.map((user: UserRecord) => (
              <AssigneeDropdownItem
                key={user.getEmail()}
                type="button"
                title={
                  user.getName()
                    ? `${user.getName()} (${user.getEmail()})`
                    : user.getEmail() ?? ''
                }
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggle(user.getEmail() ?? '')
                }}
              >
                <CheckBoxInput
                  id={`show-assignee-${modelId}-${user.getEmail()}`}
                  checked={selectedSet.has(user.getEmail() ?? '')}
                  onChange={() => toggle(user.getEmail() ?? '')}
                  onClick={(e) => e.stopPropagation()}
                  tabIndex={-1}
                  label={
                    <UserSelectLabel
                      email={user.getEmail() ?? ''}
                      name={user.getName() || user.getEmail() || ''}
                    />
                  }
                  requiredPermissions={[PERMISSIONS.READ]}
                />
              </AssigneeDropdownItem>
            ))}
          </AssigneeUserList>
        </AssigneeDropdownPanel>
      </AssigneeFieldWrap>
    </DropdownLabel>
  )
}
